import { Toaster as SonnerToaster, toast } from "sonner";
import { useTheme } from "../hooks/useTheme.js";

type ToasterProps = React.ComponentProps<typeof SonnerToaster>;

/**
 * Toast system backed by `sonner`. Mount a single `<Toaster />` near the root
 * of the app and call `toast("message")` from anywhere.
 *
 * @example
 * // Root
 * <Toaster position="bottom-right" />
 *
 * // Any component
 * import { toast } from "@/ui";
 * toast("Saved");
 * toast.success("Invite sent");
 * toast.error("Something went wrong");
 */
export function Toaster(props: ToasterProps) {
  // Sonner has its own light/dark styling for icons and the close affordance;
  // hand it the scheme the app resolved so the two never disagree.
  const { scheme } = useTheme();
  return (
    <SonnerToaster
      theme={scheme}
      {...props}
      toastOptions={{
        ...props.toastOptions,
        classNames: {
          // Glass: a toast is a transient overlay floating over the field.
          toast:
            "bg-[var(--glass-bg)] text-text-primary border border-[var(--glass-border)] " +
            "[-webkit-backdrop-filter:blur(var(--glass-blur))_saturate(1.15)] " +
            "[backdrop-filter:blur(var(--glass-blur))_saturate(1.15)] " +
            "shadow-[var(--shadow-overlay)] rounded-[var(--radius-card)] text-sm",
          description: "text-text-secondary",
          actionButton: "bg-accent text-text-inverse",
          cancelButton: "bg-[var(--print-bg)] text-text-primary",
          ...props.toastOptions?.classNames,
        },
      }}
    />
  );
}

export { toast };
